<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import paymentService from '../services/payment.service'
import bankAccountService from '../services/bankAccount.service'
import categoryService from '../services/category.service'
import ParticipantSelectorV2 from '../components/ParticipantSelectorV2.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || null)
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Check if user has permission
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canUpdatePayment = computed(() => {
  return (currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR') &&
         hasPermission('PAYMENT_UPDATE')
})

// Form data
const formData = ref({
  name: '',
  amount: '',
  days_to_pay: 7,
  bank_account_uuid: '',
  description: '',
  first_payment_date: '',
  last_payment_date: '',
  period_type: '',
  player_uuids: [] as string[]
})

const bankAccounts = ref<any[]>([])
const categories = ref<any[]>([])
const flatPlayers = ref<any[]>([])
const noCoaches = ref<string[]>([])
const loadingPayment = ref(false)
const loadingCategories = ref(false)
const loadingBankAccounts = ref(false)
const submitting = ref(false)

// Fetch recurring payment
const fetchRecurringPayment = async () => {
  if (!clubUuid.value) return

  const recurringPaymentUuid = route.params.uuid as string
  if (!recurringPaymentUuid) return

  loadingPayment.value = true
  try {
    const response = await paymentService.getRecurringPayment(clubUuid.value, recurringPaymentUuid)

    formData.value = {
      name: response.name || '',
      amount: response.amount || '',
      days_to_pay: response.days_to_pay || 7,
      bank_account_uuid: response.bank_account_uuid || '',
      description: response.description || '',
      first_payment_date: response.first_payment_date ? response.first_payment_date.split('T')[0] : '',
      last_payment_date: response.last_payment_date ? response.last_payment_date.split('T')[0] : '',
      period_type: response.period_type || '',
      player_uuids: response.players?.map((p: any) => p.uuid) || []
    }
  } catch (error: any) {
    console.error('Error fetching recurring payment:', error)
    const errorMessage = error.response?.data?.message || t('editRecurringPayment.errorFetchingPayment')
    toastStore.error(errorMessage)
    router.push('/recurring-payments')
  } finally {
    loadingPayment.value = false
  }
}

// Fetch bank accounts
const fetchBankAccounts = async () => {
  if (!clubUuid.value) return

  loadingBankAccounts.value = true
  try {
    const response = await bankAccountService.getBankAccounts(clubUuid.value)
    bankAccounts.value = response.bank_accounts || []
  } catch (error: any) {
    console.error('Error fetching bank accounts:', error)
  } finally {
    loadingBankAccounts.value = false
  }
}

// Fetch categories with players
const fetchCategories = async () => {
  if (!clubUuid.value) return

  loadingCategories.value = true
  try {
    const response = await categoryService.getCategoriesWithPlayers(clubUuid.value)
    categories.value = response.categories || []
    // Build flat player list with categories field for ParticipantSelectorV2
    const seen = new Set<string>()
    flatPlayers.value = []
    for (const cat of categories.value) {
      for (const p of (cat.players || [])) {
        if (!seen.has(p.uuid)) {
          seen.add(p.uuid)
          flatPlayers.value.push({ ...p, categories: [] })
        }
        const existing = flatPlayers.value.find((fp: any) => fp.uuid === p.uuid)
        if (existing) existing.categories.push({ uuid: cat.uuid, name: cat.name })
      }
    }
  } catch (error: any) {
    console.error('Error fetching categories:', error)
    toastStore.error(t('editRecurringPayment.errorFetchingCategories'))
  } finally {
    loadingCategories.value = false
  }
}

const selectedPlayersProxy = computed({
  get: () => formData.value.player_uuids,
  set: (val: string[]) => { formData.value.player_uuids = val }
})

// Handle form submit
const handleSubmit = async () => {
  if (!clubUuid.value) return
  if (!canUpdatePayment.value) {
    toastStore.error(t('editRecurringPayment.noPermission'))
    return
  }

  if (!formData.value.name) {
    toastStore.error(t('editRecurringPayment.enterPaymentName'))
    return
  }

  if (!formData.value.amount || parseFloat(formData.value.amount) <= 0) {
    toastStore.error(t('editRecurringPayment.enterValidAmount'))
    return
  }

  if (formData.value.player_uuids.length === 0) {
    toastStore.error(t('editRecurringPayment.selectAtLeastOnePlayer'))
    return
  }

  if (!formData.value.first_payment_date || !formData.value.last_payment_date || !formData.value.period_type) {
    toastStore.error(t('editRecurringPayment.fillRequiredFields'))
    return
  }

  submitting.value = true

  try {
    const recurringPaymentUuid = route.params.uuid as string
    await paymentService.updateRecurringPayment(clubUuid.value, recurringPaymentUuid, formData.value)

    toastStore.success(t('editRecurringPayment.paymentUpdatedSuccessfully'))
    router.push('/recurring-payments')
  } catch (error: any) {
    console.error('Error updating recurring payment:', error)
    const message = error.response?.data?.message || t('editRecurringPayment.updateError')
    toastStore.error(message)
  } finally {
    submitting.value = false
  }
}

onMounted(async () => {
  if (!canUpdatePayment.value) {
    toastStore.error(t('editRecurringPayment.noPermission'))
    router.push('/recurring-payments')
    return
  }

  await Promise.all([
    fetchRecurringPayment(),
    fetchBankAccounts(),
    fetchCategories()
  ])
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4">
      <!-- Back Button -->
      <div class="mb-4">
        <router-link to="/recurring-payments" class="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd"/>
          </svg>
          {{ t('editRecurringPayment.backToList') }}
        </router-link>
      </div>

      <!-- Page Header -->
      <div class="mb-4">
        <h1 class="text-2xl font-bold text-gray-900">{{ t('editRecurringPayment.pageTitle') }}</h1>
      </div>

      <!-- Loading Payment -->
      <div v-if="loadingPayment" class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <div class="text-center py-8">
          <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      </div>

      <!-- Form -->
      <div v-else class="bg-white border-2 border-gray-300 rounded shadow-sm p-6">
        <form @submit.prevent="handleSubmit">
          <!-- Basic Fields -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">{{ t('editRecurringPayment.basicInformation') }}</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Payment Name -->
              <div>
                <label for="paymentName" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editRecurringPayment.paymentName') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="paymentName"
                  v-model="formData.name"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Days to Pay -->
              <div>
                <label for="daysToPay" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editRecurringPayment.daysToPay') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  id="daysToPay"
                  v-model.number="formData.days_to_pay"
                  required
                  min="0"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Bank Account -->
              <div>
                <label for="bankAccount" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editRecurringPayment.bankAccount') }}
                </label>
                <select
                  id="bankAccount"
                  v-model="formData.bank_account_uuid"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option value="">{{ t('editRecurringPayment.selectBankAccount') }}</option>
                  <option v-for="account in bankAccounts" :key="account.uuid" :value="account.uuid">
                    {{ account.account_name }} - {{ account.account_number }}
                  </option>
                </select>
              </div>

              <!-- Amount -->
              <div>
                <label for="amount" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editRecurringPayment.amount') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  id="amount"
                  v-model="formData.amount"
                  required
                  min="0.01"
                  step="0.01"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Description -->
              <div class="md:col-span-2">
                <label for="description" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editRecurringPayment.description') }}
                </label>
                <textarea
                  id="description"
                  v-model="formData.description"
                  rows="4"
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                ></textarea>
              </div>

              <!-- First Payment Date -->
              <div>
                <label for="firstPaymentDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editRecurringPayment.firstPaymentDate') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  id="firstPaymentDate"
                  v-model="formData.first_payment_date"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- End Date -->
              <div>
                <label for="lastPaymentDate" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editRecurringPayment.endDate') }} <span class="text-red-600">*</span>
                </label>
                <input
                  type="date"
                  id="lastPaymentDate"
                  v-model="formData.last_payment_date"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
                >
              </div>

              <!-- Frequency -->
              <div class="md:col-span-2">
                <label for="periodType" class="block text-sm font-medium text-gray-700 mb-1">
                  {{ t('editRecurringPayment.frequency') }} <span class="text-red-600">*</span>
                </label>
                <select
                  id="periodType"
                  v-model="formData.period_type"
                  required
                  class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600 bg-white"
                >
                  <option value="">{{ t('editRecurringPayment.selectFrequency') }}</option>
                  <option value="weekly">{{ t('editRecurringPayment.weekly') }}</option>
                  <option value="biweekly">{{ t('editRecurringPayment.biweekly') }}</option>
                  <option value="triweekly">{{ t('editRecurringPayment.triweekly') }}</option>
                  <option value="monthly">{{ t('editRecurringPayment.monthly') }}</option>
                  <option value="quarterly">{{ t('editRecurringPayment.quarterly') }}</option>
                  <option value="halfyearly">{{ t('editRecurringPayment.halfyearly') }}</option>
                  <option value="yearly">{{ t('editRecurringPayment.yearly') }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Participants Section -->
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">
              {{ t('editRecurringPayment.selectPlayers') }} ({{ formData.player_uuids.length }})
            </h2>
            <div v-if="loadingCategories" class="flex items-center gap-2 py-2 text-sm text-gray-500">
              <div class="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              Ładowanie uczestników...
            </div>
            <ParticipantSelectorV2
              v-else
              :categories="categories"
              :players="flatPlayers"
              :coaches="[]"
              v-model:selectedPlayers="selectedPlayersProxy"
              v-model:selectedCoaches="noCoaches"
            />
          </div>

          <!-- Submit Button -->
          <div class="flex justify-end">
            <button
              type="submit"
              :disabled="submitting"
              class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="submitting">{{ t('editRecurringPayment.updating') }}...</span>
              <span v-else>{{ t('editRecurringPayment.updatePayment') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </main>
</template>
